import { keepPreviousData } from '@tanstack/react-query';
import {
    PortfolioEnterpriseProductConfigurationEntriesFilters,
    PortfolioEnterpriseProductConfigurationEntry,
    useRepositoryQuery,
} from '@mews-commander/data-access';
import { lt } from '@mews/framework';
import {
    EmptyStateProps,
    Link,
    Stack,
    StackItem,
    Table,
    TableActiveColumnFilters,
    TableHeader,
    TableProvider,
    useTableState,
} from '@optimus-web/core';
import { BulkAddToProperty } from '../tables/CentralProductTable/BulkAddToProperty';
import { EnterprisesProductContextProvider } from '../../context/EnterprisesProductContext';
import { useColumns } from './columns';

interface ProductPropertiesTabContentProps {
    centralProductId: string;
    centralProductName: string;
    assignedPropertyIds: string[];
}

export const CentralProductProperties = ({
    centralProductId,
    centralProductName,
    assignedPropertyIds,
}: ProductPropertiesTabContentProps) => {
    const columns = useColumns(centralProductName);

    const [tableState, setTableState] = useTableState<
        PortfolioEnterpriseProductConfigurationEntry,
        PortfolioEnterpriseProductConfigurationEntriesFilters
    >({
        pagination: {
            pageSize: 25,
        },
    });

    const { data, isFetching } = useRepositoryQuery(
        'portfolioEnterpriseProducts',
        'getConfigurationEntries',
        {
            ...tableState,
            centralProductId,
        },
        {
            placeholderData: keepPreviousData,
        }
    );

    const tableEmptyState: EmptyStateProps = {
        primaryText: lt('CentralProductPropertiesEmptyStatePrimaryText'),
        secondaryText: lt('CentralProductPropertiesEmptyStateSecondaryText'),
        maxWidth: '400px',
        action: (
            <Stack vertical spacing="300" itemAlignment="center">
                <StackItem>
                    <BulkAddToProperty propertyIds={assignedPropertyIds} productId={centralProductId} />
                </StackItem>
                <Link standalone>{lt('LearnMoreAboutProducts')}</Link>
            </Stack>
        ),
    };
    return (
        <Stack vertical>
            <TableProvider columns={columns} state={tableState} onStateChange={setTableState}>
                <EnterprisesProductContextProvider>
                    <TableHeader
                        title={lt('PropertiesNavigation')}
                        subTitle={data?.totalRowCount || '0'}
                        rightSide={
                            <BulkAddToProperty
                                buttonVariant="tertiary"
                                propertyIds={assignedPropertyIds}
                                productId={centralProductId}
                            />
                        }
                    />
                    <TableActiveColumnFilters />
                    <Table
                        fixedFirstColumn
                        data={data?.data || []}
                        amountOfRows={data?.totalRowCount ?? 0}
                        uniqueRowId="enterpriseProductId"
                        emptyState={tableEmptyState}
                        loading={isFetching}
                    />
                </EnterprisesProductContextProvider>
            </TableProvider>
        </Stack>
    );
};
